// This file has the suffix "-component" because icon.tsx creates a URL in Next
// https://nextjs.org/docs/app/api-reference/file-conventions/metadata/app-icons#generate-icons-using-code-js-ts-tsx

import { Box } from '@ironhack/design-system2/components/box';
import {
  Icon as DsIcon,
  type IconProps,
} from '@ironhack/design-system2/components/icon';
import { type IconProps as FeatherIconProps } from '@ironhack/design-system2/icons';
import dynamic from 'next/dynamic';
import { pick, pipe, prop, propOr } from 'ramda';
import { type ComponentType, type ReactElement } from 'react';

import { type DatoIconBlock } from '@/lib/datocms/types';
import { toUpperPascalCase } from '@/lib/utils';

export const getIconAsset = (
  iconBlock: Pick<DatoIconBlock, 'name'> | null,
  loading: ComponentType = () => null
): ComponentType<FeatherIconProps> =>
  dynamic(
    // @ts-expect-error —— Not sure how to fix this next/dynamic error
    () => {
      if (!iconBlock) return null;

      const iconComponentName = pipe(
        prop('name'),
        toUpperPascalCase
      )(iconBlock);

      return import('@ironhack/design-system2/icons').then(
        propOr(null, iconComponentName)
      );
    },
    { loading }
  );

const getDefaultLoader = (props: Props) => {
  const boxProps = pick(['boxSize', 'height', 'width'], props);
  const Loading = () => <Box {...boxProps} />;
  return Loading;
};

type Props = {
  icon: Pick<DatoIconBlock, 'color' | 'name'>;
  loading?: ComponentType;
} & IconProps;

export const Icon = (props: Props): ReactElement => {
  const { icon, loading = getDefaultLoader(props), ...iconProps } = props;
  const { color } = icon;

  const IconAsset = getIconAsset(icon, loading);

  return (
    <DsIcon
      as={IconAsset}
      {...(color && { color: `icon.${color}` })}
      {...iconProps}
    />
  );
};
